import type { ActivityEntry, AnalysisVersion, Goals, Meal, MealComponent, ProductMatch, Profile, UserAccount } from '../types'

const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

interface ErrorEnvelope {
  error?: {
    code?: string
    message?: string
    retryable?: boolean
    retryAfterSeconds?: number
    requestId?: string
    fieldErrors?: Record<string, string[]>
  }
  message?: string
  errors?: Record<string, string[]>
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly retryable: boolean,
    readonly fieldErrors: Record<string, string[]> = {},
    readonly requestId: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function cookie(name: string): string | null {
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

async function request<T>(path: string, init: RequestInit = {}, idempotent = false): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  headers.set('X-Requested-With', 'XMLHttpRequest')
  const token = cookie('XSRF-TOKEN')
  if (token) headers.set('X-XSRF-TOKEN', token)
  if (idempotent) headers.set('Idempotency-Key', crypto.randomUUID())
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')

  const response = await fetch(`${apiBase}${path}`, { ...init, headers, credentials: 'include' })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ErrorEnvelope
    throw new ApiError(
      body.error?.message ?? body.message ?? 'Marlova could not complete that request.',
      response.status,
      body.error?.code ?? `HTTP_${response.status}`,
      body.error?.retryable ?? false,
      body.error?.fieldErrors ?? body.errors ?? {},
      body.error?.requestId ?? response.headers.get('X-Request-ID'),
    )
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

async function csrf(): Promise<void> {
  await request<void>('/sanctum/csrf-cookie')
}

type Resource<T> = { data: T }
type ResourceCollection<T> = { data: T[]; links?: unknown; meta?: unknown }

interface ApiProfile extends Omit<Profile, 'sexForEnergy'> {
  sexForEnergyCalculation: Profile['sexForEnergy']
}

interface ApiGoal extends Omit<Goals, 'formulaVersion' | 'source'> {
  formulaVersion: string
  source: string
}

interface ApiComponent extends Omit<MealComponent, 'quantity' | 'estimatedGrams'> {
  quantity: { value: number; unit: string; estimatedGrams: number } | string
  estimatedGrams?: number
}

interface ApiProductMatch {
  id: string
  name?: string
  product_name?: string
  brand?: string
  product_brand?: string
  variant?: string | null
  product_variant?: string | null
  serving?: string
  serving_description?: string
  matchConfidence?: ProductMatch['matchConfidence']
  match_confidence?: ProductMatch['matchConfidence']
  publisher?: string
  sourceUrl?: string | null
  source_url?: string | null
  confirmedByUser?: boolean
  confirmed_by_user?: boolean
}

type ApiClarificationQuestion = string | {
  question?: unknown
  materialImpact?: unknown
} | null

interface ApiAnalysis extends Omit<AnalysisVersion, 'components' | 'clarificationQuestion' | 'productMatches' | 'promptVersion' | 'schemaVersion'> {
  components?: ApiComponent[]
  clarificationQuestion?: ApiClarificationQuestion
  productMatches?: ApiProductMatch[]
  promptVersion: string
  schemaVersion: number
}

interface ApiMeal {
  id: string
  category: Meal['category']
  sourceType: Meal['sourceType']
  status: Meal['status']
  loggedAt: string
  localDate: string
  timezone: string
  userDescription: string | null
  currentDescription: string | null
  hasImage: boolean
  imageUrl: string | null
  product: { name: string | null; brand: string | null; variant: string | null; packageSize: string | null }
  analyses?: ApiAnalysis[]
  confirmedAnalysis?: ApiAnalysis | null
  createdAt: string
}

function normalizeComponent(component: ApiComponent): MealComponent {
  const quantity = typeof component.quantity === 'string'
    ? component.quantity
    : `${component.quantity.value.toLocaleString()} ${component.quantity.unit}`.trim()
  return {
    ...component,
    quantity,
    estimatedGrams: typeof component.quantity === 'string' ? component.estimatedGrams ?? 0 : component.quantity.estimatedGrams,
  }
}

function normalizeProduct(match: ApiProductMatch): ProductMatch {
  return {
    id: match.id,
    name: match.name ?? match.product_name ?? 'Product candidate',
    brand: match.brand ?? match.product_brand ?? 'Brand not confirmed',
    variant: match.variant ?? match.product_variant ?? null,
    serving: match.serving ?? match.serving_description ?? '1 serving',
    matchConfidence: match.matchConfidence ?? match.match_confidence ?? 'not_confirmed',
    publisher: match.publisher ?? 'Manufacturer information',
    sourceUrl: match.sourceUrl ?? match.source_url ?? null,
    confirmedByUser: match.confirmedByUser ?? match.confirmed_by_user ?? false,
  }
}

function normalizeClarificationQuestion(value: ApiClarificationQuestion | undefined): string | null {
  if (typeof value === 'string') return value.trim() || null
  if (value && typeof value.question === 'string') return value.question.trim() || null
  return null
}

export function normalizeAnalysis(analysis: ApiAnalysis): AnalysisVersion {
  return {
    ...analysis,
    components: (analysis.components ?? []).map(normalizeComponent),
    clarificationQuestion: normalizeClarificationQuestion(analysis.clarificationQuestion),
    productMatches: (analysis.productMatches ?? []).map(normalizeProduct),
    promptVersion: analysis.promptVersion as AnalysisVersion['promptVersion'],
    schemaVersion: analysis.schemaVersion as AnalysisVersion['schemaVersion'],
  }
}

function unwrapAnalysis(value: ApiAnalysis | { data?: ApiAnalysis }): ApiAnalysis {
  if ('data' in value && value.data) return value.data
  return value as ApiAnalysis
}

export function normalizeMeal(meal: ApiMeal, localImageKey: string | null = null): Meal {
  const analyses = (meal.analyses ?? []).map(normalizeAnalysis)
  const confirmed = meal.confirmedAnalysis ? normalizeAnalysis(meal.confirmedAnalysis) : null
  if (confirmed && !analyses.some((analysis) => analysis.id === confirmed.id)) analyses.push(confirmed)
  const current = analyses.at(-1) ?? confirmed
  return {
    id: meal.id,
    category: meal.category,
    sourceType: meal.sourceType,
    status: meal.status,
    loggedAt: meal.loggedAt,
    localDate: meal.localDate,
    timezone: meal.timezone,
    userDescription: meal.userDescription ?? '',
    currentDescription: meal.currentDescription ?? current?.description ?? '',
    productName: meal.product.name ?? '',
    productBrand: meal.product.brand ?? '',
    productVariant: meal.product.variant ?? '',
    packageSize: meal.product.packageSize ?? '',
    imageKey: localImageKey ?? meal.imageUrl,
    analyses,
    currentAnalysisId: current?.id ?? null,
    confirmedAnalysisId: confirmed?.id ?? null,
    createdAt: meal.createdAt,
  }
}

function profileFromApi(profile: ApiProfile): Profile {
  const { sexForEnergyCalculation, ...rest } = profile
  return { ...rest, sexForEnergy: sexForEnergyCalculation }
}

function goalFromApi(goal: ApiGoal): Goals {
  return {
    ...goal,
    source: goal.source === 'manual' ? 'manual' : 'recommended',
    formulaVersion: 'mifflin-st-jeor-v1',
  }
}

export const api = {
  async register(email: string, password: string): Promise<UserAccount> {
    await csrf()
    return request<UserAccount>('/api/v1/register', { method: 'POST', body: JSON.stringify({ email, password, passwordConfirmation: password, acceptedTerms: true, confirmedAdult: true }) })
  },
  async login(email: string, password: string): Promise<UserAccount> {
    await csrf()
    return request<UserAccount>('/api/v1/login', { method: 'POST', body: JSON.stringify({ email, password }) })
  },
  me: () => request<UserAccount>('/api/v1/me'),
  logout: () => request<void>('/api/v1/logout', { method: 'POST' }),
  forgotPassword: async (email: string) => { await csrf(); return request<void>('/api/v1/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }) },
  resetPassword: async (email: string, token: string, password: string, passwordConfirmation: string) => { await csrf(); return request<void>('/api/v1/password/reset', { method: 'POST', body: JSON.stringify({ email, token, password, passwordConfirmation }) }) },
  getProfile: async () => profileFromApi((await request<Resource<ApiProfile>>('/api/v1/profile')).data),
  updateProfile: async (profile: Profile) => profileFromApi((await request<Resource<ApiProfile>>('/api/v1/profile', { method: 'PUT', body: JSON.stringify({ ...profile, sexForEnergyCalculation: profile.sexForEnergy, sexForEnergy: undefined }) })).data),
  getGoals: async () => goalFromApi((await request<Resource<ApiGoal>>('/api/v1/goals/current')).data),
  recommendGoals: async () => goalFromApi((await request<Resource<ApiGoal>>('/api/v1/goals/recommend', { method: 'POST' })).data),
  updateGoals: async (goals: Goals) => goalFromApi((await request<Resource<ApiGoal>>('/api/v1/goals/current', { method: 'PUT', body: JSON.stringify(goals) })).data),
  completeOnboarding: () => request<void>('/api/v1/onboarding/complete', { method: 'POST' }),
  listMeals: async () => (await request<ResourceCollection<ApiMeal>>('/api/v1/meals')).data.map((meal) => normalizeMeal(meal)),
  getDay: async (date: string) => {
    const day = await request<{ meals: ApiMeal[]; activities: ActivityEntry[] }>(`/api/v1/days/${date}`)
    return { meals: day.meals.map((meal) => normalizeMeal(meal)), activities: day.activities }
  },
  getMeal: async (id: string) => normalizeMeal((await request<Resource<ApiMeal>>(`/api/v1/meals/${id}`)).data),
  createMeal: async (payload: object) => normalizeMeal((await request<Resource<ApiMeal>>('/api/v1/meals', { method: 'POST', body: JSON.stringify(payload) }, true)).data),
  uploadMealImage: async (id: string, image: Blob, hash: string) => {
    const form = new FormData()
    form.append('image', image, 'meal.jpg')
    form.append('sha256', hash)
    return normalizeMeal((await request<Resource<ApiMeal>>(`/api/v1/meals/${id}/image`, { method: 'POST', body: form }, true)).data)
  },
  analyzeMeal: async (id: string) => normalizeAnalysis(unwrapAnalysis((await request<{ analysis: { data?: ApiAnalysis } | ApiAnalysis }>(`/api/v1/meals/${id}/analyze`, { method: 'POST', body: JSON.stringify({ imageVersion: 1, clientContext: { locale: navigator.language, preferredUnits: 'metric' } }) }, true)).analysis)),
  refineMeal: async (id: string, analysisVersionId: string, description: string) => normalizeAnalysis(unwrapAnalysis((await request<{ analysis: { data?: ApiAnalysis } | ApiAnalysis }>(`/api/v1/meals/${id}/refine`, { method: 'POST', body: JSON.stringify({ analysisVersionId, description }) }, true)).analysis)),
  confirmMeal: async (id: string, analysisVersionId: string) => normalizeMeal((await request<Resource<ApiMeal>>(`/api/v1/meals/${id}/confirm`, { method: 'POST', body: JSON.stringify({ analysisVersionId }) }, true)).data),
  updateMealCategory: async (id: string, category: Meal['category']) => normalizeMeal((await request<Resource<ApiMeal>>(`/api/v1/meals/${id}`, { method: 'PATCH', body: JSON.stringify({ category }) })).data),
  deleteMeal: (id: string) => request<void>(`/api/v1/meals/${id}`, { method: 'DELETE' }, true),
  deleteMealImage: (id: string) => request<void>(`/api/v1/meals/${id}/image`, { method: 'DELETE' }, true),
  confirmProductMatch: (id: string, productMatchId: string) => request<object>(`/api/v1/meals/${id}/product-match`, { method: 'POST', body: JSON.stringify({ productMatchId }) }, true),
  addActivity: async (activity: Omit<ActivityEntry, 'id'>, timezone: string) => (await request<Resource<ActivityEntry>>('/api/v1/activities', { method: 'POST', body: JSON.stringify({ ...activity, timezone }) }, true)).data,
  deleteActivity: (id: string) => request<void>(`/api/v1/activities/${id}`, { method: 'DELETE' }, true),
  exportAccount: () => request<object>('/api/v1/account/export', { method: 'POST' }, true),
  deleteAccount: (password: string) => request<void>('/api/v1/account', { method: 'DELETE', body: JSON.stringify({ password }) }, true),
}
